import { oc, eventIterator } from "@orpc/contract";
import { z } from "zod";

/**
 * Module Key Schema - modules the chat agent can operate in
 */
export const ModuleKeySchema = z.enum([
  "home",
  "workflows",
  "teams",
  "query-builder",
  "template-blocks",
  "email-templates",
  "pdf-templates",
  "brand-kit",
  "wizard",
  "collections",
  "edi",
]);

/**
 * Page Key Schema - finer-grained pages within a module that contribute
 * page-specific prompt fragments and tools. The backend page registry must
 * have an entry for every value here (enforced via Record<PageKey, ...>).
 */
export const PageKeySchema = z.enum([
  "workflow-editor",
  "workflow-detail",
  "sales-orders",
  "famous-edi-invoices",
  "famous-edi-order-confirmations",
  "famous-edi-shipments",
]);

/**
 * Structured content part for user messages that include image attachments.
 */
const MessageContentPartSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({
    type: z.literal("image"),
    imageData: z.string().min(1),
    mimeType: z.enum(["image/jpeg", "image/png"]),
  }),
]);

/**
 * Message Attachment Schema - file metadata attached to a chat message
 */
export const MessageAttachmentSchema = z.object({
  documentId: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
});

/**
 * Message Schema - conversation messages matching AI SDK pattern.
 * User messages may include a content array when an annotated screenshot
 * is attached (text part + image part).
 */
const MessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.union([z.string(), z.array(MessageContentPartSchema)]),
  attachments: z.array(MessageAttachmentSchema).optional(),
});

/**
 * Frontend tool definition — schema sent from the frontend so the AI model
 * can call tools that execute client-side (navigation, mutations via RPC
 * hooks, etc.). The backend registers these as passthrough tools whose
 * execute returns the input args unchanged; the frontend intercepts the
 * tool-result event and runs the real handler.
 *
 * When `awaitable` is true the tool is promise-backed: the backend blocks the
 * tool's execute() until the frontend POSTs a result via submitFrontendToolResult,
 * so the agent sees the frontend-computed result in the same turn. The frontend
 * runs an awaitable tool on the `tool-call` event (not `tool-result`, which the
 * backend won't emit until the bridge resolves).
 */
export const FrontendToolSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  parameters: z.record(z.string(), z.unknown()),
  awaitable: z.boolean().optional(),
  /**
   * True when the tool has no side effects. Echoed back on the tool-call event
   * as `isWrite: false` so the chat UI can present read-only agent actions
   * (navigation, filtering) differently from mutations. Absent means unknown.
   */
  readOnlyHint: z.boolean().optional(),
});
export type FrontendTool = z.infer<typeof FrontendToolSchema>;

/**
 * Chat Agent Chat Input Schema
 */
export const ChatAgentChatInputSchema = z.object({
  moduleKey: ModuleKeySchema,
  /**
   * Optional page-level identifier. Resolved server-side against the page
   * registry to layer page-specific prompt fragments and tools on top of
   * module defaults.
   */
  pageKey: PageKeySchema.optional(),
  currentPage: z.string(),
  messages: z.array(MessageSchema),
  threadId: z.string().uuid().optional(),
  moduleContext: z.record(z.string(), z.unknown()).optional(),
  frontendTools: z.array(FrontendToolSchema).optional(),
});

/**
 * Finish Reason Schema - reasons why the stream ended
 */
export const FinishReasonSchema = z.enum([
  "stop",
  "length",
  "tool-calls",
  "content-filter",
  "error",
  "other",
]);

/**
 * Agent Stream Event Schema - discriminated union with 6 event types
 */
export const AgentStreamEventSchema = z.discriminatedUnion("type", [
  // Text delta event - streamed text chunks
  z.object({
    type: z.literal("text-delta"),
    text: z.string(),
  }),
  // Tool call event - when agent invokes a tool
  z.object({
    type: z.literal("tool-call"),
    toolCallId: z.string(),
    toolName: z.string(),
    args: z.record(z.string(), z.unknown()),
    userFacingName: z.string().optional(),
    isWrite: z.boolean().optional(),
  }),
  // Tool result event - result from tool execution
  z.object({
    type: z.literal("tool-result"),
    toolCallId: z.string(),
    toolName: z.string(),
    result: z.unknown(),
  }),
  // Thread info event - thread metadata
  z.object({
    type: z.literal("thread-info"),
    threadId: z.string().uuid(),
    isNew: z.boolean(),
  }),
  // Finish event - stream completed
  z.object({
    type: z.literal("finish"),
    finishReason: FinishReasonSchema,
  }),
  // Assistant message persisted to DB - emitted after `finish` once the
  // assistant turn has been written to `ai_thread_messages`. Carries the
  // stable DB id (so the frontend can attach durable per-message feedback),
  // the Langfuse traceId for this turn, and the spanId of the AI SDK's
  // outermost `ai.streamText` observation (so user-feedback scores attach
  // to that specific observation rather than to the trace as a whole).
  z.object({
    type: z.literal("assistant-message-saved"),
    messageId: z.string().uuid(),
    traceId: z.string().nullable(),
    observationId: z.string().nullable(),
  }),
  // Draft snapshot event - progressive workflow draft state emitted after
  // each draft mutation tool completes, so the frontend can render the
  // workflow being built in real time.
  z.object({
    type: z.literal("draft-snapshot"),
    toolCallId: z.string(),
    draft: z.unknown(),
  }),
  // Tool input streaming event - emitted (throttled) while the model is
  // still generating a tool call's JSON input, before the `tool-call`
  // event fires. Carries the accumulated partial input text so the
  // frontend can progressively parse and render an incomplete spec (e.g.
  // streaming an email-template proposal into the editor/preview as it is
  // written). The final `tool-call`/`tool-result` remains the source of
  // truth and overwrites whatever partial state was applied.
  z.object({
    type: z.literal("tool-input-delta"),
    toolCallId: z.string(),
    toolName: z.string(),
    /** Accumulated partial JSON text of the tool input so far. */
    partialInput: z.string(),
  }),
  // Error event - something went wrong
  z.object({
    type: z.literal("error"),
    message: z.string(),
    code: z.string().optional(),
  }),
]);

/**
 * Yield Decision Schemas - telemetry for the generic "agent yields to
 * user" event. The first concrete consumer is the workflow-proposal
 * Apply Changes card; future yield kinds (approvals, clarifying
 * questions) reuse the same endpoint so yield-acceptance rate is a single
 * queryable span.
 */
export const YieldDecisionSchema = z.enum(["accepted", "discarded"]);

export const RecordYieldDecisionInputSchema = z.object({
  yieldId: z.string().min(1),
  toolCallId: z.string().min(1),
  toolName: z.string().min(1),
  decision: YieldDecisionSchema,
  threadId: z.string().uuid().optional(),
  /** Page that produced this yield, for "acceptance rate by page" queries. */
  pageKey: PageKeySchema.optional(),
});

export const RecordYieldDecisionOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * Page element captured alongside a screenshot — gives the agent numeric IDs
 * it can pass directly to annotate_screen. Bounding rects are stored client-side.
 */
export const PageElementSchema = z.object({
  id: z.number().int().positive(),
  text: z.string(),
  role: z.string(),
  tag: z.string(),
  location: z.string(),
});

/**
 * Submit Screenshot Schemas - frontend delivers a captured screenshot
 * to unblock the server-side capture_screen tool's Promise bridge.
 */
export const SubmitScreenshotInputSchema = z.object({
  toolCallId: z.string().min(1),
  imageData: z.string().min(1),
  mimeType: z.enum(["image/jpeg", "image/png"]),
  elements: z.array(PageElementSchema).optional(),
});

export const SubmitScreenshotOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * Submit Frontend Tool Result Schemas - generic round-trip for promise-backed
 * (awaitable) frontend tools. The frontend POSTs the handler's return value to
 * unblock the server-side passthrough tool's Promise bridge, so the agent sees
 * the result in the same turn. `result` is whatever the frontend handler returns
 * (e.g. a socket diff for a dynamic workflow-step edit).
 */
export const SubmitFrontendToolResultInputSchema = z.object({
  toolCallId: z.string().min(1),
  result: z.unknown(),
});

export const SubmitFrontendToolResultOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * How long a pending write approval stays actionable. Shared by the backend
 * pending-call expiry and the frontend approval card's local countdown so the
 * two clocks cannot drift apart.
 */
export const API_CALL_APPROVAL_TIMEOUT_MS = 180_000;

export const ApiCallDecisionInputSchema = z.object({
  pendingCallId: z.string().min(1),
});

export const ApproveApiCallOutputSchema = z.object({
  status: z.enum(["executed", "failed"]),
  resultSummary: z.unknown().optional(),
  error: z.string().optional(),
});

export const RejectApiCallOutputSchema = z.object({
  status: z.literal("rejected"),
});

/**
 * Chat History Schemas — reading back past conversations persisted in
 * `ai_threads` / `ai_thread_messages` for the signed-in user. Threads are
 * always scoped to the caller server-side; there is no cross-user access.
 */
export const ChatThreadSummarySchema = z.object({
  id: z.string().uuid(),
  /** `ai_threads.topic` when set, else derived from the first user message. */
  title: z.string(),
  /** Snippet of the most recent message, truncated server-side. */
  preview: z.string(),
  status: z.enum(["active", "closed"]),
  messageCount: z.number().int().nonnegative(),
  createdAt: z.coerce.date(),
  /** Timestamp of the newest message; null for an empty thread. */
  lastMessageAt: z.coerce.date().nullable(),
});

export const ListChatThreadsInputSchema = z.object({
  limit: z.number().int().min(1).max(50).default(20),
  offset: z.number().int().min(0).default(0),
  /** Case-insensitive substring match against message content. */
  search: z.string().trim().min(1).optional(),
});

export const ListChatThreadsOutputSchema = z.object({
  threads: z.array(ChatThreadSummarySchema),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

export const GetChatThreadInputSchema = z.object({
  threadId: z.string().uuid(),
});

/**
 * A persisted tool call replayed from history. `args`/`result` are whatever
 * survived write-time redaction — history never re-derives tool data.
 */
export const ChatThreadToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
  result: z.unknown().optional(),
});

export const ChatThreadMessageSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(["user", "assistant", "system"]),
  content: z.string(),
  createdAt: z.coerce.date(),
  toolCalls: z.array(ChatThreadToolCallSchema).optional(),
  attachments: z.array(MessageAttachmentSchema).optional(),
});

export const GetChatThreadOutputSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: z.enum(["active", "closed"]),
  createdAt: z.coerce.date(),
  messages: z.array(ChatThreadMessageSchema),
});

/**
 * Chat Agent Contracts
 */
export const chatAgentContracts = {
  chat: oc
    .input(ChatAgentChatInputSchema)
    .output(eventIterator(AgentStreamEventSchema))
    .route({
      method: "POST",
      path: "/chat-agent/chat",
    }),
  recordYieldDecision: oc
    .input(RecordYieldDecisionInputSchema)
    .output(RecordYieldDecisionOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/yield-decision",
    }),
  submitScreenshot: oc
    .input(SubmitScreenshotInputSchema)
    .output(SubmitScreenshotOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/submit-screenshot",
    }),
  submitFrontendToolResult: oc
    .input(SubmitFrontendToolResultInputSchema)
    .output(SubmitFrontendToolResultOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/submit-frontend-tool-result",
    }),
  approveApiCall: oc
    .input(ApiCallDecisionInputSchema)
    .output(ApproveApiCallOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/approve-api-call",
    }),
  rejectApiCall: oc
    .input(ApiCallDecisionInputSchema)
    .output(RejectApiCallOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/reject-api-call",
    }),
  listThreads: oc
    .input(ListChatThreadsInputSchema)
    .output(ListChatThreadsOutputSchema)
    .route({
      method: "GET",
      path: "/chat-agent/threads",
    }),
  getThread: oc
    .input(GetChatThreadInputSchema)
    .output(GetChatThreadOutputSchema)
    .route({
      method: "GET",
      path: "/chat-agent/threads/{threadId}",
    }),
};

// Export types
export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;
export type ModuleKey = z.infer<typeof ModuleKeySchema>;
export type PageKey = z.infer<typeof PageKeySchema>;
export type MessageContentPart = z.infer<typeof MessageContentPartSchema>;
export type ChatAgentChatInput = z.infer<typeof ChatAgentChatInputSchema>;
export type FinishReason = z.infer<typeof FinishReasonSchema>;
export type AgentStreamEvent = z.infer<typeof AgentStreamEventSchema>;
export type YieldDecision = z.infer<typeof YieldDecisionSchema>;
export type RecordYieldDecisionInput = z.infer<
  typeof RecordYieldDecisionInputSchema
>;
export type RecordYieldDecisionOutput = z.infer<
  typeof RecordYieldDecisionOutputSchema
>;
export type PageElement = z.infer<typeof PageElementSchema>;
export type SubmitScreenshotInput = z.infer<typeof SubmitScreenshotInputSchema>;
export type SubmitScreenshotOutput = z.infer<
  typeof SubmitScreenshotOutputSchema
>;
export type SubmitFrontendToolResultInput = z.infer<
  typeof SubmitFrontendToolResultInputSchema
>;
export type SubmitFrontendToolResultOutput = z.infer<
  typeof SubmitFrontendToolResultOutputSchema
>;
export type ChatThreadSummary = z.infer<typeof ChatThreadSummarySchema>;
export type ListChatThreadsInput = z.infer<typeof ListChatThreadsInputSchema>;
export type ListChatThreadsOutput = z.infer<typeof ListChatThreadsOutputSchema>;
export type GetChatThreadInput = z.infer<typeof GetChatThreadInputSchema>;
export type ChatThreadToolCall = z.infer<typeof ChatThreadToolCallSchema>;
export type ChatThreadMessage = z.infer<typeof ChatThreadMessageSchema>;
export type GetChatThreadOutput = z.infer<typeof GetChatThreadOutputSchema>;
